import {  Box, Typography } from '@mui/material';
import React, { createContext, CSSProperties, useState } from 'react';
import { BeatLoader, BounceLoader, CircleLoader, ClimbingBoxLoader, DotLoader, FadeLoader, GridLoader, MoonLoader, PacmanLoader, PropagateLoader, PuffLoader, RingLoader, RiseLoader, RotateLoader, ScaleLoader, SyncLoader } from 'react-spinners';
import { useTranslation } from '../context/TranslationContext';

export const LoadingContext = createContext({
  loading: false,
  setLoading: (loading: boolean) => {},
});

interface LoadingProviderProps {
  children: React.ReactNode;
}

const override: CSSProperties = {
  display: "block",
  margin: "0 auto",
  borderColor: "089981",
};

const loaders = [
  BeatLoader,
  BounceLoader,
  ClimbingBoxLoader,
  CircleLoader,
  DotLoader,
  FadeLoader,
  GridLoader,
  MoonLoader,
  PacmanLoader,
  PropagateLoader,
  PuffLoader,
  RingLoader,
  RiseLoader,
  RotateLoader,
  ScaleLoader,
  SyncLoader
];

const messageStyle: CSSProperties = {
  transition: "opacity 1s ease-in-out",
  opacity: 0,
  animation: "fadeInOut 3s infinite",
};

const keyframes = `
  @keyframes fadeInOut {
    0%, 10% { opacity: 0.2; }
    50% { opacity: 0.8; }
  }
`;

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const messages = [
    t("Loading the magic"),
    t("Summoning the data wizards"),
    t("Hold tight, data incoming"),
    t("Just a moment, making things awesome"),
    t("Data elves are at work"),
    t("Brewing some data potions"),
    t("Hang on, crunching numbers"),
    t("Data is on its way"),
    t("Almost there, stay tuned"),
    t("Fetching unicorns"),
    t("Aligning the stars"),
    t("Polishing the data crystals"),
    t("Warming up the servers"),
    t("Gathering the bytes"),
    t("Charging the flux capacitor"),
    t("Calibrating the data matrix"),
    t("Spinning up the hamster wheels"),
    t("Consulting the oracles"),
    t("Synchronizing the clocks")
  ];
  const randomMessage = messages[Math.floor(Math.random() * messages.length)];
  const randomIndex = Math.floor(Math.random() * loaders.length);

  const scale = 2;
  const loaderColor = "#089981"; // Updated loader color
  const renderLoader = () => {
    switch (randomIndex) {
      case 0:
        return <BeatLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      case 1:
        return <BounceLoader color={loaderColor} loading={true} cssOverride={override} size={60 * scale} />;
      case 2:
        return <ClimbingBoxLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      case 3:
        return <CircleLoader color={loaderColor} loading={true} cssOverride={override} size={200 * scale} />;
      case 4:
        return <DotLoader color={loaderColor} loading={true} cssOverride={override} size={60 * scale} />;
      case 5:
        return <FadeLoader color={loaderColor} loading={true} cssOverride={override} height={15 * scale} width={5 * scale} radius={2 * scale} margin={2 * scale} />;
      case 6:
        return <GridLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      case 7:
        return <MoonLoader color={loaderColor} loading={true} cssOverride={override} size={60 * scale} />;
      case 8:
        return <PacmanLoader color={loaderColor} loading={true} cssOverride={override} size={25 * scale} />;
      case 9:
        return <PropagateLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      case 10:
        return <PuffLoader color={loaderColor} loading={true} cssOverride={override} size={60 * scale} />;
      case 11:
        return <RingLoader color={loaderColor} loading={true} cssOverride={override} size={60 * scale} />;
      case 12:
        return <RiseLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      case 13:
        return <RotateLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      case 14:
        return <ScaleLoader color={loaderColor} loading={true} cssOverride={override} height={35 * scale} width={4 * scale} radius={2 * scale} margin={2 * scale} />;
      case 15:
        return <SyncLoader color={loaderColor} loading={true} cssOverride={override} size={15 * scale} />;
      default:
        return null;
    }
  };

  return (
    <LoadingContext.Provider value={{ loading, setLoading }}>
      {loading && <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        position="fixed" 
        top={0} 
        left={0} 
        right={0} 
        bottom={0} 
        bgcolor="rgba(0, 0, 0, 0.5)"
        zIndex={1300}
      >
        <style>{keyframes}</style>
        {renderLoader()}
        <Typography variant="h4" gutterBottom mt={4} style={messageStyle}>
          {randomMessage}
        </Typography>
      </Box>}
      {children}
    </LoadingContext.Provider>
  );
};
